import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { HiMenu, HiX } from "react-icons/hi";
import {
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaYoutube,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

import ThemeContext from "../../context/ThemeContext";
import Dropdown from "./Dropdown";
import MiniCart from "../MiniCart/MiniCart";
import { MenuItems } from "./MenuItems";
import { trackEvent } from "../../utils/analytics";

import myLogo from "../../assets/new_logo.webp";
import lightmode from "../../assets/light-mode.webp";
import darkmode from "../../assets/dark-mode.webp";

/**
 * NAVBAR
 * ======
 * Facelift-linja:
 * - Light mode: valkoinen pinta, tumma teksti, turkoosi CTA.
 * - Dark mode: lähes musta pinta, valkoinen teksti, hillitty border.
 * - Mobiilimenu on selkeä, kevyt ja kontrastiltaan vahva.
 */
const Navbar = () => {
  const { theme, setTheme } = useContext(ThemeContext);
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileVuokrattavatOpen, setMobileVuokrattavatOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [cartCount, setCartCount] = useState(0);

  const hoverTimer = useRef(null);

  const SOCIAL_LINKS = {
    facebook: "https://www.facebook.com/spiderlegsproductions/",
    instagram: "https://www.instagram.com/spiderlegsproductions/",
    tiktok: "https://www.tiktok.com/@spiderlegsproductions",
    youtube: "https://www.youtube.com/@spiderlegsproductions",
  };

  const desktopLinkClass =
    "relative font-customFont1 text-sm font-extrabold uppercase tracking-[0.12em] " +
    "!text-[#111827] transition hover:!text-[#0EA5A3] " +
    "dark:!text-white dark:hover:!text-primary " +
    "after:content-[''] after:absolute after:left-0 after:-bottom-1 " +
    "after:h-[2px] after:w-0 after:rounded-full after:bg-primary " +
    "after:transition-all after:duration-200 hover:after:w-full";

  const mobileLinkClass =
    "block rounded-2xl border border-[#DDE7E5] bg-white px-4 py-3 " +
    "font-customFont1 text-base font-extrabold uppercase tracking-[0.12em] " +
    "!text-[#111827] shadow-sm transition " +
    "hover:border-primary hover:bg-primary/10 hover:!text-[#111827] " +
    "focus-visible:border-primary focus-visible:bg-primary/10 focus-visible:!text-[#111827] " +
    "dark:border-darkborder dark:bg-[#111114] dark:!text-white " +
    "dark:hover:border-primary dark:hover:bg-primary/10 dark:hover:!text-white " +
    "dark:focus-visible:border-primary dark:focus-visible:bg-primary/10 dark:focus-visible:!text-white";

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    const updateCartCount = () => {
      try {
        const items = JSON.parse(localStorage.getItem("rentalCart")) || [];
        setCartCount(items.reduce((sum, i) => sum + (i.quantity || 0), 0));
      } catch {
        setCartCount(0);
      }
    };

    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (!mobile) {
        setMobileVuokrattavatOpen(false);
      }
    };

    updateCartCount();
    handleResize();

    window.addEventListener("resize", handleResize);
    window.addEventListener("storage", updateCartCount);
    window.addEventListener("focus", updateCartCount);
    window.addEventListener("cartUpdated", updateCartCount);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("storage", updateCartCount);
      window.removeEventListener("focus", updateCartCount);
      window.removeEventListener("cartUpdated", updateCartCount);
    };
  }, []);

  const closeAll = () => {
    setMenuOpen(false);
    setDropdownOpen(false);
    setMobileVuokrattavatOpen(false);
    clearTimeout(hoverTimer.current);
  };

  return (
    <>
      {/* ================= NAVBAR ================= */}
        <nav
          className="
            fixed left-0 top-0 z-[9999] h-20 w-full
            border-b border-[#DDE7E5] bg-white
            shadow-[0_10px_30px_rgba(15,23,42,0.08)]
            dark:border-darkborder dark:bg-[#050505]
            dark:shadow-[0_10px_30px_rgba(0,0,0,0.45)]
          "
        >
        <div className="mx-auto flex h-full max-w-[1400px] items-center justify-between px-4">
          {/* LOGO + TEEMA */}
          <div className="flex items-center gap-3">
            <Link
              to="/"
              onClick={closeAll}
              className="
                flex h-12 w-12 items-center justify-center rounded-2xl
                border border-border bg-card shadow-sm transition
                hover:-translate-y-0.5 hover:border-primary hover:shadow-md
                dark:border-darkborder dark:bg-darkcard
              "
              aria-label="Spiderlegs Productions etusivu"
            >
              <img
                src={myLogo}
                alt="Spiderlegs Productions logo"
                className="h-9 w-9 object-contain"
              />
            </Link>

            <button
              type="button"
              onClick={() => {
                setTheme(theme === "light" ? "dark" : "light");
                trackEvent("teema_vaihdettu", {
                  uusi_teema: theme === "light" ? "dark" : "light",
                });
              }}
              className="
                flex h-11 w-11 items-center justify-center rounded-full
                border border-border bg-surface text-text shadow-sm transition
                hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                dark:border-darkborder dark:bg-darksurface dark:text-darktext
                dark:hover:border-primary dark:hover:bg-primary/10
              "
              aria-label="Vaihda teemaa"
            >
              <img
                src={theme === "light" ? darkmode : lightmode}
                alt="Teema"
                className="h-6 w-6"
              />
            </button>
          </div>

          {/* ================= DESKTOP MENU ================= */}
          <ul className="hidden items-center gap-7 md:flex">
            <li>
              <Link to="/" onClick={closeAll} className={desktopLinkClass}>
                Koti
              </Link>
            </li>

            <li className="relative">
              <div
                onMouseEnter={() => {
                  if (isMobile) return;

                  clearTimeout(hoverTimer.current);
                  hoverTimer.current = setTimeout(() => {
                    setDropdownOpen(true);
                  }, 120);
                }}
                onMouseLeave={() => {
                  if (isMobile) return;

                  hoverTimer.current = setTimeout(() => {
                    setDropdownOpen(false);
                  }, 180);
                }}
              >
                <Link
                  to="/vuokrattavat"
                  onClick={closeAll}
                  className={desktopLinkClass}
                >
                  Vuokrattavat
                </Link>

                <AnimatePresence>
                  {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, x: "-50%" }}
                    animate={{ opacity: 1, y: 0, x: "-50%" }}
                    exit={{ opacity: 0, y: 8, x: "-50%" }}
                    transition={{ duration: 0.16 }}
                    className="fixed left-1/2 top-20 z-[9999]"
                  >
                    <Dropdown onLinkClick={closeAll} />
                  </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </li>

            <li>
              <Link to="/meista" onClick={closeAll} className={desktopLinkClass}>
                Meistä
              </Link>
            </li>

            <li>
              <Link to="/UKK" onClick={closeAll} className={desktopLinkClass}>
                UKK
              </Link>
            </li>

            <li>
              <Link
                to="/tee-varaus"
                onClick={closeAll}
                className="
                  inline-flex min-h-11 items-center justify-center rounded-full
                  border border-primary bg-primary px-6 py-2
                  font-customFont1 text-sm font-extrabold uppercase tracking-[0.12em]
                  !text-darkest shadow-sm transition
                  hover:-translate-y-0.5 hover:bg-accent hover:border-accent hover:shadow-md
                  dark:border-primary dark:bg-primary dark:!text-darkest
                  dark:hover:bg-accent dark:hover:border-accent
                "
              >
                Tee varaus
              </Link>
            </li>
          </ul>

          {/* OIKEA */}
          <div className="flex items-center gap-3">
            <MiniCart cartCount={cartCount} />

            <button
              type="button"
              className="
                flex h-11 w-11 items-center justify-center rounded-full
                border border-border bg-card text-text shadow-sm transition
                hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                dark:border-darkborder dark:bg-darkcard dark:text-darktext
                dark:hover:border-primary dark:hover:bg-primary/10
                md:hidden
              "
              onClick={() => {
                setMenuOpen((v) => !v);
                trackEvent("mobiilimenu_toggle");
              }}
              aria-label={menuOpen ? "Sulje valikko" : "Avaa valikko"}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <HiX size={28} /> : <HiMenu size={28} />}
            </button>
          </div>
        </div>
      </nav>

      {/* ================= MOBIILIMENU ================= */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobilemenu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="
              fixed inset-0 z-[9998] bg-white md:hidden
              dark:bg-[#050505]
            "
          >
            {/* Taustaoverlay */}
          <div
            aria-hidden
            className="
              absolute inset-0
              bg-[radial-gradient(circle_at_top_left,rgba(24,214,190,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(109,40,217,0.10),transparent_34%)]
              dark:bg-[radial-gradient(circle_at_top_left,rgba(24,214,190,0.10),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(109,40,217,0.18),transparent_34%)]
            "
          />

            <motion.div
              initial={{ y: 34 }}
              animate={{ y: 0 }}
              exit={{ y: 34 }}
              transition={{ duration: 0.26, ease: "easeOut" }}
              className="relative h-full overflow-y-auto px-6 pb-10 pt-24"
            >
              {/* Mobiilimenun yläosa */}
              <div className="mb-8 flex flex-col items-center text-center">
                <div
                  className="
                    mb-4 flex h-24 w-24 items-center justify-center rounded-[2rem]
                    border border-border bg-card/90 shadow-lg
                    dark:border-darkborder dark:bg-darkcard/90
                  "
                >
                  <img
                    src={myLogo}
                    alt="Spiderlegs Productions logo"
                    className="h-16 w-16 object-contain"
                  />
                </div>

                <span
                  className="
                    rounded-full border border-primary/40 bg-primary/10 px-4 py-2
                    font-customFont1 text-xs font-extrabold uppercase tracking-[0.18em]
                    text-text
                    dark:text-darktext
                  "
                >
                  Navigaatio
                </span>
              </div>

              <ul className="mx-auto max-w-sm space-y-3 text-center">
                <li>
                  <Link to="/" onClick={closeAll} className={mobileLinkClass}>
                    Koti
                  </Link>
                </li>

                {/* MOBIILI ACCORDION */}
                <li className="text-center">
                  <div
                    className="
                      rounded-3xl border border-border bg-card/92 p-3 shadow-sm
                      dark:border-darkborder dark:bg-darkcard/92
                    "
                  >
                    <div className="flex items-center justify-between gap-3">
                      <Link
                        to="/vuokrattavat"
                        onClick={closeAll}
                          className="
                            flex-1 rounded-2xl px-4 py-3 text-left
                            font-customFont1 text-base font-extrabold uppercase
                            tracking-[0.12em] !text-[#111827] transition
                            hover:bg-primary/10 hover:!text-[#111827]
                            dark:!text-white dark:hover:bg-primary/10 dark:hover:!text-white
                          "
                      >
                        Vuokrattavat
                      </Link>

                      <button
                        type="button"
                        onClick={() => setMobileVuokrattavatOpen((v) => !v)}
                        aria-label="Avaa vuokrattavat-alavalikko"
                        aria-expanded={mobileVuokrattavatOpen}
                        className="
                          flex h-11 w-11 shrink-0 items-center justify-center rounded-full
                          border border-border bg-surface text-text transition
                          hover:border-primary hover:bg-primary/10
                          dark:border-darkborder dark:bg-darksurface dark:text-darktext
                          dark:hover:border-primary dark:hover:bg-primary/10
                        "
                      >
                        <motion.span
                          className="inline-block text-2xl leading-none"
                          animate={{
                            rotate: mobileVuokrattavatOpen ? 180 : 0,
                          }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                          ▾
                        </motion.span>
                      </button>
                    </div>

                    {/* ALAVALIKKO */}
                    <AnimatePresence>
                      {mobileVuokrattavatOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22 }}
                          className="mt-3 overflow-hidden"
                        >
                          <div className="space-y-1 border-t border-border pt-3 dark:border-darkborder">
                          {MenuItems.map((group) => {
                              const subItems =
                                group.subMenu1 || group.subMenu2 || group.subMenu3 || [];

                              return (
                                <div
                                  key={group.id}
                                  className="
                                    mb-4 rounded-2xl border border-[#DDE7E5] bg-white p-3 text-left
                                    dark:border-darkborder dark:bg-[#111114]
                                  "
                                >
                                  <Link
                                    to={group.path}
                                    onClick={closeAll}
                                    className="
                                      mb-2 inline-flex rounded-full border border-[#DDE7E5] bg-[#F8FAFC]
                                      px-3 py-1.5 font-customFont1 text-[11px] font-extrabold uppercase
                                      tracking-[0.10em] !text-[#111827]
                                      dark:border-darkborder dark:bg-darksurface dark:!text-white
                                    "
                                  >
                                    {group.title}
                                  </Link>

                                  <div className="space-y-1">
                                    {subItems.map((item) => (
                                      <Link
                                        key={item.id}
                                        to={item.slug ? `/tuote/${item.slug}` : item.path}
                                        onClick={closeAll}
                                        className="
                                          block rounded-xl px-3 py-2
                                          font-customFont1 text-sm font-semibold leading-snug
                                          !text-[#374151] transition
                                          hover:bg-primary/10 hover:!text-[#111827]
                                          dark:!text-[#D1D5DB] dark:hover:bg-primary/10 dark:hover:!text-white
                                        "
                                      >
                                        {item.title}
                                        {item.isNew && (
                                          <span
                                            className="
                                              ml-2 inline-flex rounded-full bg-accent px-2 py-0.5
                                              text-[10px] font-extrabold uppercase !text-[#111827]
                                            "
                                          >
                                            Uusi
                                          </span>
                                        )}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </li>

                <li>
                  <Link
                    to="/meista"
                    onClick={closeAll}
                    className={mobileLinkClass}
                  >
                    Meistä
                  </Link>
                </li>

                <li>
                  <Link to="/UKK" onClick={closeAll} className={mobileLinkClass}>
                    UKK
                  </Link>
                </li>

                <li>
                  <Link
                    to="/tee-varaus"
                    onClick={closeAll}
                      className="
                        mt-2 inline-flex min-h-12 w-full items-center justify-center
                        rounded-full border border-primary bg-primary px-6 py-3
                        font-customFont1 text-base font-extrabold uppercase
                        tracking-[0.12em] !text-[#111827] shadow-md transition
                        hover:bg-accent hover:border-accent hover:!text-[#111827] hover:shadow-lg
                        focus-visible:bg-accent focus-visible:border-accent focus-visible:!text-[#111827]
                      "
                  >
                    Tee varaus
                  </Link>
                </li>
              </ul>

              {/* Kevyt jakaja */}
              <div className="my-10 flex justify-center">
                <div className="h-px w-40 bg-gradient-to-r from-transparent via-primary to-transparent" />
              </div>

              {/* SOSIAALINEN MEDIA */}
              <div
                className="
                  flex items-center justify-center gap-4 text-2xl
                  text-text dark:text-darktext
                "
              >
                <a
                  href={SOCIAL_LINKS.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook – Spiderlegs Productions"
                  title="Spiderlegs Productions Facebookissa"
                  onClick={() => trackEvent("some_facebook_klikattu")}
                  className="
                    flex h-11 w-11 items-center justify-center rounded-full
                    border border-border bg-card transition
                    hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                    dark:border-darkborder dark:bg-darkcard
                  "
                >
                  <FaFacebookF />
                </a>

                <a
                  href={SOCIAL_LINKS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram – Spiderlegs Productions"
                  title="Spiderlegs Productions Instagramissa"
                  onClick={() => trackEvent("some_instagram_klikattu")}
                  className="
                    flex h-11 w-11 items-center justify-center rounded-full
                    border border-border bg-card transition
                    hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                    dark:border-darkborder dark:bg-darkcard
                  "
                >
                  <FaInstagram />
                </a>

                <a
                  href={SOCIAL_LINKS.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok – Spiderlegs Productions"
                  title="Spiderlegs Productions TikTokissa"
                  onClick={() => trackEvent("some_tiktok_klikattu")}
                  className="
                    flex h-11 w-11 items-center justify-center rounded-full
                    border border-border bg-card transition
                    hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                    dark:border-darkborder dark:bg-darkcard
                  "
                >
                  <FaTiktok />
                </a>

                <a
                  href={SOCIAL_LINKS.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube – Spiderlegs Productions"
                  title="Spiderlegs Productions YouTubessa"
                  onClick={() => trackEvent("some_youtube_klikattu")}
                  className="
                    flex h-11 w-11 items-center justify-center rounded-full
                    border border-border bg-card transition
                    hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                    dark:border-darkborder dark:bg-darkcard
                  "
                >
                  <FaYoutube />
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;